import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { ErrorWarningConstants } from "../../constants/ErrorWarningConstants";
import { FormattedMessage } from "react-intl";
import {
  ModalBody,
  Modal,
  ModalHeaderWithTitleAndClose,
  LightButton,
  PrimaryButton,
  StickyFooterButtons,
  ModalFooter
} from "best-common-react";
import CheckboxWarning from "../profile/checkInModal/CheckboxWarning";

const ConfirmMessage = styled.div.attrs(() => ({
  className: "mb-3"
}))`
  font-size: 1.125rem;
  font-weight: 400;
`;

const Hint = styled.div`
  color: #ccc;
  font-size: 80%;
`;

const isRequired = <FormattedMessage id="ErrorWarningModal.isRequired" defaultMessage="is a required field." />;
const unableToCheckIn = (
  <FormattedMessage id="ErrorWarningModal.unableToCheckIn" defaultMessage="Unable to Check In Prospect" />
);
const checkboxOverrideButton = (
  <FormattedMessage id="ErrorWarningModal.checkboxOverrideButton" defaultMessage="Check In" />
);
const cancel = <FormattedMessage id="ErrorWarningModal.cancel" defaultMessage="Cancel" />;
const close = <FormattedMessage id="ErrorWarningModal.close" defaultMessage="Close" />;
const bulkTitle = <FormattedMessage id="ErrorWarningModal.bulkTitle" defaultMessage="Results" />;
const ignore = <FormattedMessage id="ErrorWarningModal.ignore" defaultMessage="Ignore" />;
const error = <FormattedMessage id="ErrorWarningModal.error" defaultMessage="Error" />;
const warning = <FormattedMessage id="ErrorWarningModal.warning" defaultMessage="Warning" />;
const bulkError = (
  <FormattedMessage id="ErrorWarningModal.bulkError" defaultMessage="Please fix the following errors:" />
);

const getModalText = (isOverrideable, isOverrideableCheckbox, code) => {
  if (isOverrideableCheckbox) {
    // overrideable warning using a checkbox
    return {
      title: error,
      subtitle: "",
      overrideButton: checkboxOverrideButton,
      button: cancel
    };
  } else if (code === ErrorWarningConstants.CODES.OVERDUE_PERIOD_CHECK) {
    // check for specific error code, this should probably be avoided to make everything more generic, but leaving it for now
    return {
      title: unableToCheckIn,
      subtitle: "",
      overrideButton: ignore,
      button: close
    };
  } else if (isOverrideable) {
    // regular overrideable warning
    return {
      title: warning,
      subtitle: "",
      overrideButton: ignore,
      button: close
    };
  } else {
    // hard error
    return {
      title: error,
      subtitle: "",
      overrideButton: ignore,
      button: close
    };
  }
};

const renderOverrideableWarning = (message, hintMessage) => (
  <li key={message}>
    <div>{message}</div>
    <Hint>{hintMessage}</Hint>
  </li>
);

const renderErrors = (errors, language) => {
  if (!errors) {
    return null;
  }

  // Bulk Errors
  if (errors.length && errors.length > 0 && errors[0].errors) {
    errors = errors.reduce((allErrors, bulk) => {
      allErrors = allErrors.concat(bulk.errors);
      return allErrors;
    }, []);
  }

  const requiredFieldErrors = errors.filter(error => error.code === ErrorWarningConstants.CODES.REQUIRED_FIELD);

  if (requiredFieldErrors && requiredFieldErrors.length > 0) {
    return requiredFieldErrors.map((error, i) => (
      <li key={i}>
        {error.messageDetail ? error.messageDetail + ":" : ""} {getFieldIdentifier(error, language)} {isRequired}
      </li>
    ));
  }

  return errors.map((error, i) => <li key={i}>{getMessage(error, language)}</li>);
};

// Get the message (english, spanish) from an error/warning
const getMessage = (item, language) => {
  if (language === "es" && item.spanishMessage) {
    return item.spanishMessage;
  }
  return item.message;
};

const getFieldIdentifier = (item, language) => {
  if (language === "es" && item.spanishFieldIdentifier) {
    return item.spanishFieldIdentifier;
  }
  return item.fieldIdentifier;
};

class ErrorWarningModal extends Component {
  state = {
    isCheckboxChecked: false
  };

  onCheckboxChange = ({ target: { checked } }) => {
    this.setState({ isCheckboxChecked: checked });
  };

  renderWarnings = (warnings, language) => {
    return warnings.map(warning => {
      let message = getMessage(warning, language);
      let hintText = null;
      return renderOverrideableWarning(message, hintText);
    });
  };

  componentDidUpdate({ show: prevShow }) {
    if (!prevShow && this.props.show) {
      this.setState({ isCheckboxChecked: false });
    }
  }

  render() {
    const { rootStore, show = false, onClose } = this.props;
    const { errorWarningStore, translationsStore } = rootStore;
    const language = translationsStore.getCurrentLanguage();
    const isOverrideableCheckbox =
      errorWarningStore.overrideHandler &&
      errorWarningStore.overrideCheckbox &&
      errorWarningStore.overrideCheckbox.enabled;
    const modalText = getModalText(
      !!errorWarningStore.overrideHandler,
      isOverrideableCheckbox,
      errorWarningStore.errorWarningCode
    );
    const buttonDisabled = isOverrideableCheckbox && !this.state.isCheckboxChecked;
    return (
      <Modal show={show} size="lg">
        <ModalHeaderWithTitleAndClose
          title={errorWarningStore.hasBulkResponses ? bulkTitle : modalText.title}
          close={() => errorWarningStore.closeModal()}
        />
        <ModalBody>
          <div>
            {!errorWarningStore.hasBulkResponses ? (
              <React.Fragment>
                <ConfirmMessage>{modalText.subtitle}</ConfirmMessage>
                <ul>
                  {renderErrors(errorWarningStore.errors, language)}
                  {this.renderWarnings(errorWarningStore.warnings, language)}
                  {isOverrideableCheckbox && (
                    <CheckboxWarning
                      key="checkbox-override"
                      message={errorWarningStore.overrideCheckbox.message}
                      hint={errorWarningStore.overrideCheckbox.hint}
                      onChange={this.onCheckboxChange}
                    />
                  )}
                </ul>
              </React.Fragment>
            ) : (
              <div>
                <ConfirmMessage>{errorWarningStore.getBulkResponseMessage}</ConfirmMessage>
                <ConfirmMessage>{bulkError}</ConfirmMessage>
                <ul>{renderErrors(errorWarningStore.bulkResponse.errors, language)}</ul>
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <StickyFooterButtons>
            {errorWarningStore.overrideHandler && (
              <PrimaryButton
                onClick={() => {
                  errorWarningStore.overrideHandler();
                  errorWarningStore.closeModal();
                }}
                disabled={buttonDisabled}
              >
                {modalText.overrideButton}
              </PrimaryButton>
            )}
            <LightButton
              onClick={() => {
                errorWarningStore.closeModal();
              }}
            >
              {modalText.button}
            </LightButton>
          </StickyFooterButtons>
        </ModalFooter>
      </Modal>
    );
  }
}

ErrorWarningModal.propTypes = {
  rootStore: PropTypes.object.isRequired,
  show: PropTypes.bool,
  onClose: PropTypes.func
};

export default inject("rootStore")(observer(ErrorWarningModal));
